import dayjs from 'dayjs';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import { getCalendarDays } from '../utils';

type CalendarGridProps = {
  countByDay: Record<string, number>;
  onSelectDay: (day: string) => void;
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function CalendarGrid({ countByDay, onSelectDay }: CalendarGridProps) {
  const translate = useTranslate();
  const days = getCalendarDays();
  if (days.length === 0) return null;

  const firstDow = dayjs(days[0]).day();
  const leadingPadding = Array.from({ length: firstDow }, (_, i) => `pad-${i}`);

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
        }}
      >
        {WEEKDAYS.map((d) => (
          <Typography
            key={d}
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', fontWeight: 600, textTransform: 'uppercase' }}
          >
            {d}
          </Typography>
        ))}
        {leadingPadding.map((key) => (
          <Box key={key} />
        ))}
        {days.map((day) => {
          const parsed = dayjs(day);
          const count = countByDay[day] ?? 0;
          return (
            <Paper
              key={day}
              variant="outlined"
              onClick={() => onSelectDay(day)}
              sx={{
                cursor: 'pointer',
                p: 1.5,
                minHeight: 88,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'background 0.15s, border-color 0.15s',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main',
                },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  {parsed.format('D')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {parsed.format('MMM')}
                </Typography>
              </Stack>
              {count > 0 ? (
                <Chip
                  label={translate('module.worldCup.breakingEvents.calendar.eventCount', {
                    smart_count: count,
                  })}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ alignSelf: 'flex-start' }}
                />
              ) : (
                <Typography variant="caption" color="text.disabled">
                  {translate('module.worldCup.breakingEvents.calendar.empty')}
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Stack>
  );
}
